import WorldSystem from '../WorldSystem';
import { BoosterType } from '../../../State/Card/BoosterType';
import { RewardType } from '../../../Utility/RewardHelper';
import TaskQueue from '../../../Utility/Task/TaskQueue';
import { IPlayerStateTaskData } from '../../../State/Player/IPlayerState';
import GameObject from '../../../GameObject/GameObject';
import WorldHUDHandler from '../../UI/HUD/WorldHUDHandler';
import Renderable from '../../../Component/Renderable';
import AnimationClip from '../../Animation/AnimationClip';
import { PlayAnimation } from '../../Animation/AnimationHelper';
import { RewardCoins } from '../../Animation/Clip/RewardCoins';
import { RewardBoosters } from '../../Animation/Clip/RewardBoosters';
import { RewardStarChestStars } from '../../Animation/Clip/RewardStarChestStars';
import System from 'System/System';
import Animator from 'Prototype/Animator';
import Game from 'Game';
import { GameScene } from 'State/Game/GameScene';
import { FarmRewardCoins } from 'System/Animation/Clip/FarmRewardCoins';

type QueueableSystem = System & { taskQueue: TaskQueue<unknown>; animator: Animator; };

export default class RewardAnimationHelper {
    public static coins(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target?: GameObject
    ): void {
        const clip = Game.store.getState().activeScene.result === GameScene.FARM ? FarmRewardCoins : RewardCoins;
        this.queueAnimation(
            system,
            new clip(system.animator, {
                scene: system.scene,
                value: taskData.coins,
                previousValue: taskData.previousCoins,
                target
            }),
            taskData.queueNext
        );
    }

    public static boosterTNT(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: BoosterType.TNT,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static boosterBullsEye(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: BoosterType.BULLS_EYE,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static boosterShuffle(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: BoosterType.SHUFFLE,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static boosterUndo(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: BoosterType.UNDO,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static jokers(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: BoosterType.JOKER,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static freePlay(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: RewardType.FREE_PLAY,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static rewardAnyBooster(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: taskData.type,
                amount: taskData.amount,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    public static starChestStars(
        system: WorldSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        const starChestProgressBar: GameObject = GameObject.findFirstByName(
            WorldHUDHandler.STAR_CHEST_PROGRESS_BAR
        );

        let sourceObject: GameObject;

        if (taskData.levelNumber) {
            sourceObject = system.getLevelNode(taskData.levelNumber);
        }

        if (!sourceObject || !sourceObject.getComponent(Renderable).visible) {
            sourceObject = starChestProgressBar.find('off_screen_star_spawn');
        }

        this.queueAnimation(
            system,
            new RewardStarChestStars(starChestProgressBar, {
                sourceObject,
                amount: taskData.amount,
                previousStarCount: taskData.previousStarCount,
                currentStarChestTier: taskData.currentStarChestTier,
                system,
            }),
            taskData.queueNext
        );
    }

    public static extraCards(
        system: QueueableSystem,
        taskData: IPlayerStateTaskData,
        target: GameObject
    ): void {
        this.queueAnimation(
            system,
            new RewardBoosters(system.animator, {
                type: taskData.type || BoosterType.PLUS_ONE,
                amount: taskData.cards,
                target,
                animateDown: taskData.animateDown,
            }),
            taskData.queueNext
        );
    }

    private static queueAnimation(
        system: { taskQueue: TaskQueue<unknown> },
        clip: AnimationClip,
        queueNext?: boolean
    ) {
        if (queueNext) {
            system.taskQueue.queueNext({
                execute: () => PlayAnimation(clip),
            });
        } else {
            system.taskQueue.queue({
                execute: () => PlayAnimation(clip),
            });
        }
    }
}
